/**
 * friendly-error.ts — the one place a staff-API failure becomes a sentence a person can act on
 * (punchlist #21, 18 July 2026).
 *
 * ⭐ WHY THIS EXISTS AS A MODULE. The mapping previously lived as TWO near-identical
 * `humaniseError` closures inside UserManagementPage (one per drawer), and BOTH ended with
 * `return msg` — so any code neither of them knew about was rendered to the user verbatim. The
 * reported symptom was one instance:
 *
 *     admin-api 409 on /admin/subscribers/<id>/memberships: {"error":"membership_exists"}
 *
 * Two things were wrong there, and only one of them was `membership_exists`:
 *   1. the fall-through returned the raw string, so EVERY unmapped code leaked identically; and
 *   2. the string named `admin-api` — a service DELETED by ADR-099 W6b — so the user was shown
 *      the name of something that no longer exists (fixed at source in lib/api-client.ts).
 *
 * Adding a `membership_exists` case alone would have fixed exactly one string and left the
 * mechanism intact. So the contract here is inverted: **an unmapped code returns a generic
 * sentence, never the raw text.** The worst case is now vague, not raw. Diagnosis is preserved
 * by logging the raw message to the console.
 *
 * ⚠ NEVER render `err.message` directly. It carries the status, the path and the response body.
 * Route every failure through this function — inline error states included, not just toasts.
 */

import { MAX_TARGET_COUNT } from './growth-regions';

export interface FriendlyErrorContext {
  /** Names the organisation a membership message concerns ("already a member of <org>"). */
  nodeLabel?: string;
}

/**
 * Matching is by SUBSTRING against the error message, which embeds the server's `error.code`
 * (see `StaffApiError`). Substring rather than equality is deliberate: it keeps working
 * whether the caller passes `err.message`, `err.code`, or a bare string, so no call site can
 * be subtly wrong about which it holds.
 */
export function friendlyStaffError(msg: string, ctx?: FriendlyErrorContext): string {
  // ── Session / permission ──────────────────────────────────────────────────
  if (msg.includes('stale_auth')) return 'Your session has expired — please sign in again.';
  if (msg.includes('no_id_token')) return 'Your session has expired — please sign in again.';
  if (msg.includes('insufficient_role')) return 'You do not have permission to make this change.';
  if (msg.includes('self_action_forbidden')) return 'You cannot change your own account.';

  // ── Last-admin guardrails ─────────────────────────────────────────────────
  //
  // ⛔ `last_account_admin` REMOVED (ADR-112 D5). No server path returns it any
  // more: removing or downgrading an account's last real admin is now ALLOWED,
  // because the permanent sentinel row anchors the account instead of a refusal.
  // The old sentence ("assign another Entity Admin before…") instructed staff to
  // satisfy a precondition that no longer exists, so keeping it would be worse
  // than the generic fallback, not better. `last_super_admin` is a STAFF-pool
  // rule (identity service) and is untouched — as is the subscriber self-service
  // `OWNER_PROTECTED` rule in team.ts.
  if (msg.includes('last_super_admin')) return 'This is the last Super Admin — promote another before changing this one.';

  // ── Operator memberships (ADR-112 D7; ADR-113 D25 Am.3) ───────────────────
  //
  // ⚠ THE POINTER CHANGED WITH THE MERGE. These used to send staff to the Portal
  // Access tab. Operator rows are now managed in the person's own drilldown —
  // the access sections directly below the memberships editor — so a message
  // sending them to another tab would be a wrong instruction, not just a stale
  // one. `operator_membership_readonly` still fires for the MALFORMED row (an
  // `operator_*` role on an entity node), which is a data fault rather than a
  // surface boundary, so it says which.
  if (msg.includes('operator_membership_readonly')) return 'This row’s role and organisation type disagree — it is read-only until the record is corrected.';
  if (msg.includes('operator_org_not_addable')) return 'Operator organisations are not membership targets — use Appoint, below, to attach someone to one.';

  // ── ADR-113 D25 Amendment 3 — the appoint / assign half ───────────────────
  //
  // ⛔⛔ `client_disassociated` IS THE ONE THAT MATTERS MOST, and it must never
  // read as a system fault. The CUSTOMER closed this relationship; only they can
  // re-open it. Writing the assignment anyway would create a `CASSIGN#` row that
  // looks like access and reaches nothing — so the message has to make clear that
  // the refusal is correct and that the next step is not the staff member's.
  if (msg.includes('client_disassociated')) return 'That subscriber has closed this consultant relationship — only they can re-open it, from their own account.';
  // ⛔ Commercial policy, stated in the server's own words (Jeff, 26 September 2026; ADR-116).
  // The sentence IS the server's message — surfaced verbatim, never softened to "not found".
  if (msg.includes('cross_zone_assignment')) return 'Consultants serve customers in their own data region only.';
  if (msg.includes('operator_not_found')) return 'That person no longer holds this operator attachment — refresh to see the current list.';
  if (msg.includes('operator_already_enrolled')) return 'That person is already attached to this organisation.';
  if (msg.includes('email_belongs_to_another_person')) return 'That email belongs to a different person — the appointment was not made against the person on this screen.';
  if (msg.includes('not_a_customer_account')) return 'That is an operator organisation, not a subscriber account — a consultant can only be assigned to a subscriber.';
  // D10 (5 October 2026) — the signature of a call sent to the wrong zone: the account lives in
  // another zone, so the zone asked has no record of it. Point the operator at the zone in the header.
  if (msg.includes('account_not_found')) return 'We couldn’t find this account in the zone we looked in — check the zone shown in the header.';
  if (msg.includes('customer_not_found')) return 'No subscriber account with that id — check the id and try again.';
  if (msg.includes('last_partner_admin')) return 'This is the partner organisation’s last active admin — promote another before removing or suspending this one.';
  if (msg.includes('advocate_org_not_found')) return 'That Advocate organisation no longer exists — refresh to see the current list.';
  if (msg.includes('account_membership_not_owned_here')) return 'Account memberships are managed in the memberships list above, not in the access sections.';
  if (msg.includes('invalid_role_for_portal')) return 'That role is not valid for this kind of organisation.';
  if (msg.includes('partner_org_not_found')) return 'No partner organisation with that id — check the id and try again.';

  // ── Memberships ───────────────────────────────────────────────────────────
  // The reported case. Names the org so the reader knows WHICH membership already exists,
  // rather than being told "a" membership exists and having to work out which.
  if (msg.includes('membership_exists')) {
    return ctx?.nodeLabel
      ? `This user is already a member of ${ctx.nodeLabel}.`
      : 'This user is already a member of that organisation.';
  }
  if (msg.includes('membership_not_found')) return 'That membership no longer exists — refresh to see the current list.';
  if (msg.includes('membership_missing_node')) return 'That membership is missing its organisation — refresh and try again.';

  // ── Validation ────────────────────────────────────────────────────────────
  if (msg.includes('invalid_role_for_tier')) return 'That role is not valid for the selected node tier.';
  if (msg.includes('invalid_node_type')) return 'That organisation type is not valid for a membership.';
  if (msg.includes('invalid_role')) return 'That role is not one this account can assign.';
  if (msg.includes('confirm_mismatch')) return 'The confirmation email did not match.';
  // ⚠ LAST IN THIS BLOCK, DELIBERATELY. `invalid_request` is what a server returns when it
  // cannot say anything more specific, so any code that CAN be specific must be matched
  // before it. It is still worth a sentence of its own: it says the form's contents were
  // refused — which is a thing to re-check — where the generic fall-through says only that
  // something, somewhere, went wrong and invites a pointless retry of the same values.
  if (msg.includes('invalid_request')) return 'Some of the details on this form were not accepted — check the values and try again.';

  // ── Transport: the request never got an answer ────────────────────────────
  //
  // ⭐ THESE ARE NOT SERVER CODES — `api-client.ts` mints them when `fetch` itself
  // rejects, so no amount of server-side mapping could ever have covered them, and they
  // were the most common codes with no sentence of their own. The distinction between
  // them is the operator's next move, which is why they do not share one message:
  // a timeout MAY STILL BE RUNNING server-side (the 30s client bound is shorter than the
  // Lambda's), so "try again" risks a duplicate; a network error reached nothing at all,
  // so retrying is exactly right.
  if (msg.includes('request_timeout')) return 'This is taking longer than expected — it may still be running. Wait a moment and refresh before trying again.';
  if (msg.includes('network_error')) return 'Could not reach the server — check your connection and try again.';

  // ── Growth · Step 1 harvest ───────────────────────────────────────────────
  //
  // ⭐⭐ `target_count_out_of_range` IS THE ONE THAT CAUSED THIS WHOLE FILE TO BE REVISITED.
  // An operator asked for 100 records against a server cap of 50; the API refused it
  // correctly and the refusal arrived as the generic sentence, so a well-formed request
  // read as an outage and was diagnosed for a day as a jurisdiction bug that had never
  // fired.
  //
  // ⚠ THE NUMBER COMES FROM THE SHARED CONSTANT, NOT FROM THE RESPONSE. The server does
  // send `max` in the body, but `StaffApiError` keeps only the `error` CODE — the body is
  // discarded — so reading the cap back off `msg` would be a branch that can never be
  // taken, silently degrading to a vaguer sentence forever. `MAX_TARGET_COUNT` is
  // parity-tested against the server's own constant instead.
  if (msg.includes('target_count_out_of_range')) {
    return `Cohorts are capped at ${MAX_TARGET_COUNT} candidates per request — ask for ${MAX_TARGET_COUNT} or fewer.`;
  }
  // ⭐⭐ The free-text location box's half of the same jurisdiction defect. `jurisdiction`
  // defaults to AU, so typing a US location while leaving the country alone ran AU search
  // parameters against a US SERP. The server refuses that contradiction outright.
  //
  // ⚠ THE SENTENCE CANNOT NAME THE PLACE, AND THAT IS A PROPERTY OF THE TRANSPORT, NOT AN
  // OVERSIGHT. The API does send `place` and `placeJurisdiction` in the body, but
  // `StaffApiError` keeps only the `error` CODE — so a branch that read them back would be
  // dead code that silently degraded to this sentence anyway (the same trap documented on
  // `target_count_out_of_range` below). It therefore names the two FIELDS instead, which
  // the operator can see on screen, rather than a value it cannot obtain.
  if (msg.includes('location_jurisdiction_mismatch')) {
    return 'The location you entered is in a different country from the one selected — '
      + 'change the country to match, or enter a location inside it.';
  }
  // The 503 from `handleHarvest` when no Serper key is provisioned. Deliberately points at
  // configuration rather than suggesting a retry: retrying cannot provision a secret, and
  // "try again in a moment" would send the operator round a loop that cannot terminate.
  if (msg.includes('serper_unavailable')) return 'Search is not configured for this environment — an administrator needs to provision the search provider key.';

  // ── Not found ─────────────────────────────────────────────────────────────
  if (msg.includes('staff_not_found')) return 'That staff member no longer exists — refresh to see the current list.';
  if (msg.includes('subscriber_not_found')) return 'That person no longer exists — refresh to see the current list.';

  // ⛔ THE FALL-THROUGH. Never `return msg`.
  console.error('staff-portal: unmapped API error', msg);
  return GENERIC_FALLBACK;
}

const GENERIC_FALLBACK = 'Something went wrong. Please try again in a moment.';

/** Convenience for a caught `unknown` — the shape every catch block actually holds. */
export function friendlyFromUnknown(err: unknown, ctx?: FriendlyErrorContext): string {
  return friendlyStaffError(err instanceof Error ? err.message : String(err), ctx);
}

// =============================================================================
// Customer actions — say WHAT failed, WHERE, WHY, and give a REFERENCE (6 October 2026)
// =============================================================================
//
// ⭐⭐ JEFF'S RULE, 6 OCTOBER 2026: A VAGUE TOAST IS A DEFECT. Cancelling Arcana's subscription
// (US zone) showed "Could not change the subscription — Something went wrong. Please try again in
// a moment." The cancellation had in fact LANDED; the toast could tell the operator neither that,
// nor where it ran, nor why anything failed, nor how to find the request. So every error toast on
// the Customers detail page is now built by `actionErrorToast`, and it always carries:
//   title  — the action and its target:  "Cancel subscription failed — Arcana · us-east-1 · United States"
//   detail — the reason in words (from the server's code), then "Reference: <request id>"
// "Something went wrong" survives ONLY when the server returned no code at all, and even then the
// title still names the action and the zone, and the detail still carries a reference line.
//
// ⚠ THE TABLE IS EXACT-MATCH ON THE CODE, and `customer-action-codes.test.ts` enumerates every
// code the account service's customer-action handlers can emit (read from the server source) and
// fails if one has no line here. Add the sentence when you add the code.

/** Where an action ran — what the toast names. */
export interface ActionErrorContext {
  /** The action as the operator pressed it: "Cancel subscription", "Delete account". */
  action: string;
  /** The record: the business name. */
  subject: string;
  /** The zone it was sent to, operator form: "us-east-1 · United States". */
  zoneLabel: string;
}

/** What a sentence can draw on: the refusal's own fields and the context. */
interface SentenceInput {
  details: Readonly<Record<string, unknown>>;
  zoneLabel: string;
}

type Sentence = string | ((i: SentenceInput) => string);

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * One plain-language line per code the customer-action routes can return (account service:
 * admin-customers, admin-subscription-state, admin-domains, billing-inject, the staff dispatch
 * gate and parse-body), plus the codes the client itself mints for an unanswered request.
 */
export const CUSTOMER_ACTION_SENTENCES: Readonly<Record<string, Sentence>> = {
  // ── Session / permission (the staff dispatch gate) ──
  stale_auth: 'Your session has expired — please sign in again.',
  unauthorized: 'Your session has expired — please sign in again.',
  no_id_token: 'Your session has expired — please sign in again.',
  insufficient_role: 'You do not have permission to make this change.',
  not_found: 'The server does not recognise this action — it may be running an older version. Quote the reference.',
  method_not_allowed: 'The server does not accept this action on this record.',

  // ── Which record ──
  account_not_found: ({ zoneLabel }) =>
    `There is no such account in ${zoneLabel} — check the zone shown in the header.`,
  missing_account_id: 'The request did not say which account — refresh the page and try again.',
  missing_domain_token: 'The request did not say which domain — refresh the page and try again.',
  domain_not_found: 'That domain no longer exists on this account — refresh to see the current list.',
  invoice_not_found: 'That invoice no longer exists — refresh the Billing tab.',
  invalid_cursor: 'The list position has expired — refresh the page.',

  // ── The form itself ──
  missing_body: 'Some of the details on this form were not accepted — check the values and try again.',
  invalid_json: 'Some of the details on this form were not accepted — check the values and try again.',
  validation_failed: 'Some of the details on this form were not accepted — check the values and try again.',
  reason_required: 'This change needs a reason — enter one and try again.',
  confirm_required: 'Type the business name or the account email to confirm.',
  confirm_mismatch: 'What you typed does not match — type the business name or the account email exactly.',
  invalid_url: 'That is not a valid website address.',
  invalid_credit: 'That credit amount was not accepted — check the figure.',

  // ── State machine / concurrency ──
  invalid_transition: ({ details }) => {
    const from = str(details['from']);
    const to = str(details['to']);
    return from && to
      ? `A subscription can’t go from ${from} to ${to}.`
      : 'That change is not allowed from the record’s current state — refresh to see where it stands.';
  },
  invalid_state: 'That domain is not in a state that allows this action — refresh to see its current status.',
  conflict: 'Someone else changed this record at the same moment — refresh and try again.',

  // ── Subscription follow-up steps (a 200 with `warnings`, ADR-013 Amendment 1) ──
  domain_cascade_failed: 'Its domains could not be updated, so its website is still collecting data. Repeat the action to finish — it is safe to repeat.',
  domain_cascade_partial: ({ details }) => {
    const n = typeof details['domainsNotUpdated'] === 'number' ? details['domainsNotUpdated'] : 0;
    return `${n || 'Some'} of its domains could not be updated and are still collecting data. Repeat the action to finish — it is safe to repeat.`;
  },

  // ── Billing (via the billing service) ──
  jurisdiction_missing: 'This account has no declared country, so billing refuses to change it. Set the country on the account first.',
  product_not_priced: 'That plan has no price in this zone’s catalogue, so it cannot be billed until it is priced.',
  no_subscription: 'That domain has no subscription to change.',
  no_upcoming_charge: 'There is no upcoming charge to act on.',
  invoice_already_reversed: 'This invoice has already been reversed.',
  consultant_budget_exceeded: ({ details }) =>
    str(details['message']) || 'You’ve reached your monthly credit budget. It resets at the start of next month.',
  billing_unavailable: 'Billing could not be reached to confirm there is no active subscription — try again shortly.',
  subscription_active: 'The subscription is still active — cancel it on the Subscription tab first (no refunds), then delete the account.',

  // ── Operator organisations (ADR-112 D4) ──
  operator_org_not_deletable: 'This is a platform organisation, not a customer — it cannot be deleted.',
  operator_org_not_creditable: 'This is a platform organisation, not a customer — it cannot be credited.',
  operator_org_not_editable: 'This is a platform organisation, not a customer — it holds no plan.',
  plan_assignment_not_supported: 'Plans can be removed here, not assigned.',

  // ── Users / domains ──
  already_associated: 'That person is already a user on this account.',
  user_already_exists: 'A login with that email already exists — associate the existing person instead.',
  identity_in_other_zone: 'That email already signs in to Nexanomics in another data region, and a login lives in one region only. Use a different email address, or work with the person in their own region.',
  domain_already_registered: 'That domain is already registered to an account.',
  no_challenge_issued: 'No verification has been started for this domain — issue the challenge first.',
  challenge_expired: 'The verification challenge has expired — issue a new one and try again.',
  challenge_mismatch: 'The site did not show the expected verification value — check it was published, then try again.',
  live_snippet_domains: 'One of this account’s domains still has the tracking snippet live — review the listed domains and tick the acknowledgement to continue.',

  // ── Delete (ADR-079 clean delete) — each stops BEFORE the account's records are removed ──
  membership_index_incomplete: 'The delete stopped before removing anything: the account’s user list could not be read completely. Try again shortly.',
  membership_resolution_failed: 'The delete stopped before removing anything: we could not tell which logins belong to other accounts. Try again shortly.',
  cognito_delete_failed: 'The delete stopped: a login could not be removed, so the account’s records were left in place. Quote the reference.',
  billing_cleanup_failed: 'The delete stopped before removing anything: the billing records could not be tidied (invoices are kept, the rest removed). Try again shortly, and quote the reference if it repeats.',
  // ADR-137 D7 — the business's Outcome Report, outcome story and credit go with it; the community service could not be reached.
  community_cleanup_failed: 'The delete stopped before removing anything: the business\'s outcome story and Outcome Report records could not be removed. Try again shortly, and quote the reference if it repeats.',

  // ── The server failed without saying why ──
  internal_error: ({ zoneLabel }) => `The server in ${zoneLabel} hit an unexpected error and gave no reason. Quote the reference.`,
  server_error: ({ zoneLabel }) => `The server in ${zoneLabel} hit an unexpected error and gave no reason. Quote the reference.`,

  // ── No answer at all (minted by api-client, never by a server) ──
  request_timeout: ({ zoneLabel }) =>
    `The ${zoneLabel} zone did not answer in time. It may still have run — refresh before trying again.`,
  network_error: ({ zoneLabel }) => `Could not reach the ${zoneLabel} zone — check your connection and try again.`,
};

interface ErrorParts {
  code: string;
  requestId: string;
  details: Readonly<Record<string, unknown>>;
}

function partsOf(err: unknown): ErrorParts {
  const e = err as Partial<ErrorParts> | null;
  if (e && typeof e === 'object' && typeof e.code === 'string') {
    return {
      code: e.code,
      requestId: typeof e.requestId === 'string' ? e.requestId : '',
      details: e.details && typeof e.details === 'object' ? e.details : {},
    };
  }
  // Not a StaffApiError (a test double, or an Error built elsewhere): recover a known code from the
  // message — the module's long-standing substring convention — so the reason is still in words.
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  const known = Object.keys(CUSTOMER_ACTION_SENTENCES)
    .filter((c) => new RegExp(`\\b${c}\\b`).test(msg))
    .sort((a, b) => b.length - a.length)[0];
  return { code: known ?? '', requestId: '', details: {} };
}

/** The reason line for one code, in words. Unmapped codes are logged loudly and still say which
 *  code arrived (a code is not a raw error: it is the server's own name for the refusal). */
export function reasonForCode(code: string, input: SentenceInput): string {
  const s = CUSTOMER_ACTION_SENTENCES[code];
  if (typeof s === 'function') return s(input);
  if (typeof s === 'string') return s;
  // A code from another surface (memberships, operator attachments…) may already have a sentence
  // in the older substring mapper above — use it before admitting there is none.
  const older = friendlyStaffError(code);
  if (older !== GENERIC_FALLBACK) return older;
  console.error('staff-portal: UNMAPPED customer-action error code — add it to CUSTOMER_ACTION_SENTENCES', code);
  return `The server refused this (${code.replace(/_/g, ' ')}).`;
}

/** "Reference: <id>" — or why there is none. */
function referenceLine(p: ErrorParts): string {
  if (p.requestId) return `Reference: ${p.requestId}`;
  if (p.code === 'request_timeout' || p.code === 'network_error') return 'Reference: none — the request got no answer.';
  return `Reference: none returned — sent ${new Date().toISOString()}.`;
}

/**
 * The toast for a failed customer action: the action, the target (business · zone), the reason in
 * words, and a reference.
 */
export function actionErrorToast(err: unknown, ctx: ActionErrorContext): { title: string; detail: string } {
  // Diagnostic only — the raw message carries status, path and body, so it goes to the console.
  console.error('staff-portal: customer action failed', ctx.action, err instanceof Error ? err.message : String(err));
  const p = partsOf(err);
  const title = `${ctx.action} failed — ${ctx.subject} · ${ctx.zoneLabel}`;
  const reason = p.code
    ? reasonForCode(p.code, { details: p.details, zoneLabel: ctx.zoneLabel })
    : `Something went wrong in ${ctx.zoneLabel} — the server gave no reason.`;
  return { title, detail: `${reason} ${referenceLine(p)}` };
}

/**
 * A change that LANDED with a follow-up step unfinished (a 200 carrying `warnings`). The title says
 * the change happened; the detail says what did not finish and how to finish it.
 */
export function actionWarningToast(
  warnings: ReadonlyArray<{ code: string } & Record<string, unknown>>,
  ctx: ActionErrorContext & { done: string; requestId?: string },
): { title: string; detail: string } {
  const reasons = warnings.map((w) => reasonForCode(w.code, { details: w, zoneLabel: ctx.zoneLabel }));
  const ref = ctx.requestId ? ` Reference: ${ctx.requestId}` : '';
  return {
    title: `${ctx.done} — ${ctx.subject} · ${ctx.zoneLabel}, but not finished`,
    detail: `${reasons.join(' ')}${ref}`,
  };
}
